import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Container, AppBar, Toolbar, Typography, IconButton, Box } from '@mui/material';
import { Computer, Logout } from '@mui/icons-material';
import ServerList from './components/ServerList';
import ServerManagement from './components/ServerManagement';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import { authApi } from './services/api';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
});

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [username, setUsername] = useState<string | null>(null);

  // Check authentication status on mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await authApi.checkAuth();
      if (response.success && response.data.authenticated) {
        setIsAuthenticated(true);
        setUsername(response.data.username || null);
      } else {
        setIsAuthenticated(false);
        setUsername(null);
      }
    } catch (error) {
      console.error('Auth check error:', error);
      setIsAuthenticated(false);
      setUsername(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginSuccess = () => {
    checkAuthStatus();
  };

  const handleLogout = async () => {
    try {
      await authApi.logout();
      setIsAuthenticated(false);
      setUsername(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <BrowserRouter>
      <ThemeProvider theme={theme}>
        <CssBaseline />

        {isAuthenticated && (
          <AppBar position="static">
            <Toolbar>
              <Computer sx={{ mr: 2 }} />
              <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                Dedicated Manager - Управление выделенными серверами
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {username && (
                  <Typography variant="body2">
                    {username}
                  </Typography>
                )}
                <IconButton
                  color="inherit"
                  onClick={handleLogout}
                  title="Выход"
                >
                  <Logout />
                </IconButton>
              </Box>
            </Toolbar>
          </AppBar>
        )}

        {!isAuthenticated && !isLoading ? (
          <Login onLoginSuccess={handleLoginSuccess} />
        ) : (
          <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
              <Routes>
                <Route path="/" element={<ServerList />} />
                <Route path="/server/:serviceName" element={<ServerManagement />} />
              </Routes>
            </Container>
          </ProtectedRoute>
        )}
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;